// src/components/layout/Header.tsx
"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import Container from "./Container";
import SearchBar from "@/components/search/SearchBar";
import ThemeToggle from "@/components/layout/ThemeToggle";

function NavLink({ href, label }: { href: string; label: string }) {
  const pathname = usePathname();
  const active = pathname === href || (href !== "/" && pathname.startsWith(href));

  return (
    <Link
      href={href}
      className={[
        "rounded-md px-3 py-2 text-sm font-medium transition",
        active
          ? "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100"
          : "text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-neutral-100",
      ].join(" ")}
    >
      {label}
    </Link>
  );
}

export default function Header() {
  const pathname = usePathname();

  const hideSearch =
    pathname.startsWith("/admin") ||
    (pathname.startsWith("/products/") && pathname !== "/products");

  // Hard URLs (your Cloud Run image endpoints)
  const LOGO_DARK =
    "https://valueport-api-484254764546.northamerica-northeast1.run.app/api/images/f9ba5e7217e54b10875ad91efb2a6492";
  const LOGO_LIGHT =
    "https://valueport-api-484254764546.northamerica-northeast1.run.app/api/images/d5bc9668250248c7afd1ac3c272b251c";

  const LOGO_H = "h-14";
  const LOGO_W = "w-[160px]";
  const LOGO_FIT = "object-contain";
  const LOGO_POS = "object-center";

  return (
    <header className="sticky top-0 z-50 border-b border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950">
      <Container className="flex h-16 items-center gap-3">
        {/* Logo (CSS-driven, no theme state) */}
        <Link href="/" className="flex items-center">
          <div className={`relative ${LOGO_H} ${LOGO_W} overflow-hidden`}>
            {/* Light logo */}
            <img
              src={LOGO_LIGHT}
              alt="ValuePort"
              draggable={false}
              className={[
                "h-full w-full select-none",
                LOGO_FIT,
                LOGO_POS,
                "block dark:hidden",
              ].join(" ")}
            />
            {/* Dark logo */}
            <img
              src={LOGO_DARK}
              alt="ValuePort"
              draggable={false}
              className={[
                "h-full w-full select-none",
                LOGO_FIT,
                LOGO_POS,
                "hidden dark:block",
              ].join(" ")}
            />
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-1 md:flex">
          <NavLink href="/" label="Home" />
          <NavLink href="/products" label="Products" />
          <NavLink href="/categories" label="Categories" />
        </nav>

        {/* Right side */}
        <div className="ml-auto flex w-full max-w-xl items-center justify-end gap-2">
          {!hideSearch && (
            <div className="hidden w-full md:block">
              <SearchBar />
            </div>
          )}
          <ThemeToggle />
        </div>
      </Container>

      {/* Mobile Nav */}
      <div className="border-t border-neutral-200 dark:border-neutral-800 md:hidden">
        <Container className="flex items-center gap-2 py-2">
          <NavLink href="/" label="Home" />
          <NavLink href="/products" label="Products" />
          <NavLink href="/categories" label="Categories" />
          {!hideSearch && (
            <div className="ml-auto w-full max-w-xs">
              <SearchBar />
            </div>
          )}
        </Container>
      </div>
    </header>
  );
}
